import { postDataAPI } from './fetchData';

/**
 * Search for an artist's Bandsintown profile and fetch their upcoming show dates
 * @param {string} artistName - The name of the artist to search for
 * @returns {Promise<{success: boolean, bookings: [], artistName: string, bandsintownUrl: string}>}
 */
export const searchBandsintownForArtist = async (artistName) => {
  try {
    if (!artistName || typeof artistName !== 'string' || artistName.trim() === '') {
      return {
        success: false,
        bookings: [],
        artistName: '',
        bandsintownUrl: null,
        error: 'Artist name is required'
      };
    }

    console.log(`🔍 Searching Bandsintown for: ${artistName}`);

    const response = await postDataAPI('user/bookings/bandsintown/search', {
      artistName: artistName.trim()
    });

    if (response.data?.status === 200) {
      const data = response.data?.data || response.data;
      return {
        success: data.success || false,
        bookings: data.bookings || [],
        artistName: data.artistName || artistName.trim(),
        bandsintownUrl: data.bandsintownUrl || null
      };
    }

    return {
      success: false,
      bookings: [],
      artistName: artistName.trim(),
      bandsintownUrl: null,
      error: response.data?.message || 'Failed to search Bandsintown'
    };
  } catch (error) {
    console.error('❌ Error searching Bandsintown:', error);
    return {
      success: false,
      bookings: [],
      artistName: artistName || '',
      bandsintownUrl: null,
      error: error?.response?.data?.message || error?.message || 'Error connecting to service'
    };
  }
};

/**
 * Import Bandsintown bookings to the database
 * @param {Array} bookings - Array of booking objects to import
 * @param {string} userId - Optional user ID (will use current user if not provided)
 * @returns {Promise<{success: boolean, savedCount: number}>}
 */
export const importBandsintownBookings = async (bookings, userId = null) => {
  try {
    if (!bookings || !Array.isArray(bookings) || bookings.length === 0) {
      return {
        success: false,
        savedCount: 0,
        error: 'Bookings array is required'
      };
    }

    console.log(`📥 Importing ${bookings.length} Bandsintown bookings...`);
    
    // Get token explicitly
    const token = localStorage.getItem('dizeeToken');
    if (!token) {
      console.error('❌ No authentication token available for import');
      return {
        success: false,
        savedCount: 0,
        error: 'Authentication required. Please log in.'
      };
    }
    
    console.log('🔑 Token found, making import request...');

    const response = await postDataAPI('user/bookings/bandsintown/import', {
      bookings,
      userId
    }, token);
    
    console.log('📊 Import response:', {
      status: response?.status,
      dataStatus: response?.data?.status,
      hasData: !!response?.data?.data,
      success: response?.data?.data?.success,
      savedCount: response?.data?.data?.savedCount
    });

    if (response.data?.status === 200) {
      const data = response.data?.data || response.data;
      return {
        success: data.success || false,
        savedCount: data.savedCount || 0,
        bookings: data.bookings || []
      };
    }

    return {
      success: false,
      savedCount: 0,
      error: response.data?.message || 'Failed to import bookings'
    };
  } catch (error) {
    console.error('❌ Error importing Bandsintown bookings:', error);
    console.error('❌ Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status
    });
    return {
      success: false,
      savedCount: 0,
      error: error?.response?.data?.message || error?.message || 'Error connecting to service'
    };
  }
};

/**
 * Import Bandsintown bookings via AI web search (uses Puppeteer scraping)
 * This function searches for shows using the AI chat endpoint and imports them
 * @param {string} artistName - The name of the artist to search for
 * @returns {Promise<{success: boolean, savedCount: number}>}
 */
export const importBandsintownViaAI = async (artistName) => {
  try {
    if (!artistName || typeof artistName !== 'string' || artistName.trim() === '') {
      return {
        success: false,
        savedCount: 0,
        error: 'Artist name is required'
      };
    }

    console.log(`🤖 Importing Bandsintown shows via AI for: ${artistName}`);

    // Get authentication token
    const token = localStorage.getItem('dizeeToken');
    if (!token) {
      console.error('❌ No authentication token available');
      return {
        success: false,
        savedCount: 0,
        error: 'Authentication required. Please log in.'
      };
    }

    // Call the AI chat endpoint to trigger web search for shows
    console.log('🔍 Searching for Bandsintown shows via AI web search...');
    
    let searchResponse;
    try {
      searchResponse = await postDataAPI("user/chatWithAIForDizee", {
        // Keep behavior the same, but be explicit about confirmed status, city/state, and avoiding duplicates.
        prompt: `Find shows for ${artistName} on Bandsintown and add them to my bookings app with status confirmed (so they appear in the confirmed tab), including the correct city and state/province for each show from Bandsintown, and avoiding duplicate bookings if they already exist.`,
        conversation: [],
        conversationId: ''
      }, token);
    } catch (apiError) {
      console.error('❌ API call failed:', apiError);
      return {
        success: false,
        savedCount: 0,
        error: `API request failed: ${apiError?.response?.data?.message || apiError.message || 'Unknown error'}`
      };
    }

    console.log('📊 AI Search Response:', {
      hasData: !!searchResponse?.data,
      hasNestedData: !!searchResponse?.data?.data,
      hasResponse: !!searchResponse?.data?.data?.response,
      responsePreview: searchResponse?.data?.data?.response?.substring(0, 100)
    });

    if (!searchResponse?.data?.data?.response) {
      console.log('⚠️ No response from AI search');
      return {
        success: false,
        savedCount: 0,
        error: 'Failed to get response from AI search'
      };
    }

    console.log('✅ Bandsintown search completed via AI');

    // Check if we can extract show data from the function results
    const functionResults = searchResponse.data.data.actions_taken;
    const conversationId = searchResponse.data.data.conversationId;
    
    console.log('📊 Conversation ID:', conversationId);
    if (!functionResults || !Array.isArray(functionResults)) {
      console.log('⚠️ No function results in AI response');
      return {
        success: false,
        savedCount: 0,
        error: 'No show data extracted from search'
      };
    }

    const webSearchResult = functionResults.find(action => action.type === 'search' && action.shows);

    if (!webSearchResult || !webSearchResult.shows || webSearchResult.shows.length === 0) {
      console.log('ℹ️ AI search completed but no shows were extracted');
      console.log('📊 Function results breakdown:', functionResults.map(r => ({
        type: r.type,
        hasShows: !!r.shows,
        showCount: r.shows?.length || 0
      })));
      return {
        success: true,
        savedCount: 0,
        message: 'No upcoming shows found on Bandsintown'
      };
    }

    console.log(`🎉 Found ${webSearchResult.shows.length} shows from Bandsintown`);
    console.log('📊 Show data sample:', webSearchResult.shows[0]);

    // Transform shows to booking format
    const bookings = webSearchResult.shows.map(show => ({
      artist: artistName,
      perf_date: show.date,
      venue: show.venue,
      city: show.city || '',
      state: show.state || '',
      country: show.country || '',
      status: 'confirmed',
      ticket_link: show.ticketLink || '',
      source: 'bandsintown',
      bandsintown_id: show.ticketLink ? show.ticketLink.split('/').pop().split('?')[0] : ''
    }));
    
    console.log('📊 Transformed bookings sample:', bookings[0]);
    console.log(`📥 Preparing to import ${bookings.length} bookings...`);

    // Import the bookings
    const importResult = await importBandsintownBookings(bookings);

    if (importResult.success && importResult.savedCount > 0) {
      console.log(`✅ Successfully imported ${importResult.savedCount} shows from Bandsintown`);
      
      // Clean up the Bandsintown import conversation so it doesn't interfere with tour prompt
      if (conversationId) {
        console.log('🧹 Deleting Bandsintown import conversation to preserve tour prompt...');
        try {
          const { deleteDataAPI } = await import('./fetchData');
          await deleteDataAPI(`user/conversations/${conversationId}`, token);
          console.log('✅ Bandsintown import conversation deleted');
        } catch (deleteError) {
          console.log('⚠️ Could not delete conversation (non-critical):', deleteError.message);
        }
      }
      
      return {
        success: true,
        savedCount: importResult.savedCount,
        bookings: importResult.bookings
      };
    }

    return {
      success: false,
      savedCount: 0,
      error: importResult.error || 'Failed to import shows'
    };
  } catch (error) {
    console.error('❌ Error in importBandsintownViaAI:', error);
    return {
      success: false,
      savedCount: 0,
      error: error?.response?.data?.message || error?.message || 'Error connecting to service'
    };
  }
};

